/**
 * AI视频创作平台 - HTTP 请求封装
 * 基于 axios 封装统一的请求工具
 */

import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { message } from 'ant-design-vue';
import type { ApiResponse } from '@shared/types';
import { useUserStore } from '@/stores/user';
import { useLoginModal } from '@/composables/useLoginModal';
import { getAuthParams } from '@/lib/csd-adapter';

// 扩展 AxiosRequestConfig 类型,添加自定义配置
declare module 'axios' {
  export interface AxiosRequestConfig {
    /** 是否跳过全局错误提示(默认false) */
    skipErrorHandler?: boolean;
    /** 标记是否为重试请求（内部使用） */
    _isRetry?: boolean;
  }
}

// 是否正在刷新Token（防止并发刷新）
let isRefreshing = false;
// 等待刷新完成的请求队列
let refreshQueue: Array<{ resolve: (token: string) => void; reject: (error: Error) => void }> = [];

/**
 * 登录已失效时，统一清理状态并拉起登录弹窗
 */
function handleLoginExpired(messageText = '登录已过期'): Error {
  const userStore = useUserStore();
  userStore.clearUser();
  const { goToLogin } = useLoginModal();
  goToLogin();
  return new Error(messageText);
}

/**
 * 尝试刷新Token并重发请求
 * 支持并发请求：多个401同时发生时只刷新一次
 */
async function handleTokenRefresh(failedConfig: InternalAxiosRequestConfig): Promise<AxiosResponse> {
  const userStore = useUserStore();
  
  if (!isRefreshing) {
    isRefreshing = true;
    try {
      const success = await userStore.refreshAccessToken();
      if (success && userStore.accessToken) {
        // 刷新成功，通知所有等待的请求
        refreshQueue.forEach(({ resolve }) => resolve(userStore.accessToken!));
        refreshQueue = [];
        // 用新token重发当前请求
        failedConfig.headers.Authorization = `Bearer ${userStore.accessToken}`;
        failedConfig._isRetry = true;
        return instance(failedConfig);
      } else {
        // 刷新失败
        refreshQueue.forEach(({ reject }) => reject(new Error('Token刷新失败')));
        refreshQueue = [];
        return Promise.reject(handleLoginExpired());
      }
    } finally {
      isRefreshing = false;
    }
  } else {
    // 已经在刷新中，排队等待
    return new Promise<AxiosResponse>((resolve, reject) => {
      refreshQueue.push({
        resolve: (token: string) => {
          failedConfig.headers.Authorization = `Bearer ${token}`;
          failedConfig._isRetry = true;
          resolve(instance(failedConfig));
        },
        reject: (error: Error) => reject(error),
      });
    });
  }
}

// 创建 axios 实例
const instance: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000, // 30秒超时
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器
instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 迁移期兼容本地 JWT；没有 JWT 时由 CSD Token 完成认证
    const userStore = useUserStore();
    if (userStore.accessToken) {
      config.headers.Authorization = `Bearer ${userStore.accessToken}`;
    }
    
    // 附加 CSD Token 和设备参数（供后端调用 360qnw API）
    try {
      const authParams = getAuthParams();
      if (authParams.token) {
        config.headers['X-CSD-Token'] = authParams.token;
      }
      if (authParams.ekey) {
        config.headers['X-CSD-Ekey'] = authParams.ekey;
        config.headers['X-CSD-Device-Code'] = authParams.device_code;
        config.headers['X-CSD-Soft-Id'] = authParams.soft_id;
      }
    } catch {
      // CSD SDK 未初始化时忽略
    }
    return config;
  },
  error => {
    console.error('请求配置错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
// 注意：拦截器返回 ApiResponse 对象，而不是 AxiosResponse
// 使用 any 类型绕过 TypeScript 的严格检查，因为我们改变了返回类型
instance.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>): any => {
    const { data, config } = response;

    // 检查业务状态码
    if (data.code !== 0) {
      // 401 处理：先尝试刷新Token再重发
      if (data.code === 401) {
        const userStore = useUserStore();
        // 如果有 refreshToken 且不是重试请求，尝试刷新
        if (userStore.refreshToken && !(config as any)._isRetry) {
          return handleTokenRefresh(config as InternalAxiosRequestConfig);
        }
        // 没有 refreshToken 或重试后仍然 401，清除登录状态并弹出登录弹窗
        return Promise.reject(handleLoginExpired(data.message));
      }
      
      // 如果配置了跳过全局错误处理,则不显示消息
      if (!config.skipErrorHandler) {
        message.error(data.message || '请求失败');
      }
      
      return Promise.reject(new Error(data.message));
    }

    // 返回完整的 ApiResponse 对象，这样组件中可以直接使用 res.code 和 res.data
    return data;
  },
  error => {
    const { config } = error;
    const serverMessage = error.response?.data?.message;
    
    // 如果配置了跳过全局错误处理,则不显示消息
    if (config?.skipErrorHandler) {
      if (serverMessage) {
        error.message = serverMessage;
      }
      return Promise.reject(error);
    }

    // 网络错误处理
    if (error.code === 'ECONNABORTED') {
      message.error('请求超时，请稍后再试');
    } else if (!error.response) {
      message.error('网络连接失败，请检查网络');
    } else {
      const status = error.response.status;
      switch (status) {
        case 400:
          // 业务校验错误，优先显示后端返回的详细信息
          message.error(serverMessage || '请求参数错误');
          break;
        case 401:
          // 未授权：先尝试刷新Token再重发
          {
            const userStore = useUserStore();
            if (userStore.refreshToken && !config?._isRetry) {
              return handleTokenRefresh(config as InternalAxiosRequestConfig);
            }
            // 刷新失败或无 refreshToken，清除登录状态并弹出登录弹窗
            return Promise.reject(handleLoginExpired(serverMessage || '登录已过期'));
          }
          break;
        case 403:
          message.error(serverMessage || '没有操作权限');
          break;
        case 404:
          message.error(serverMessage || '请求的资源不存在');
          break;
        case 429:
          message.error('请求过于频繁，请稍后再试');
          break;
        case 500:
          message.error('服务器内部错误，请稍后再试');
          break;
        case 503:
          // 服务不可用，显示后端详细信息
          message.error(serverMessage || '服务暂时不可用，请稍后再试');
          break;
        default:
          message.error(serverMessage || `请求失败 (${status})`);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * 封装 GET 请求
 * @param url 请求地址
 * @param params 查询参数
 * @param config 额外配置
 * @returns 响应数据（ApiResponse.data 部分）
 */
export async function get<T>(
  url: string,
  params?: Record<string, unknown>,
  config?: AxiosRequestConfig
): Promise<T> {
  // 响应拦截器返回的是 ApiResponse 对象，这里需要类型断言
  const response = await instance.get(url, { params, ...config }) as unknown as ApiResponse<T>;
  return response.data;
}

/**
 * 封装 POST 请求
 * @param url 请求地址
 * @param data 请求体数据
 * @param config 额外配置
 * @returns 响应数据（ApiResponse.data 部分）
 */
export async function post<T>(
  url: string,
  data?: Record<string, unknown>,
  config?: AxiosRequestConfig
): Promise<T> {
  // 响应拦截器返回的是 ApiResponse 对象，这里需要类型断言
  const response = await instance.post(url, data, config) as unknown as ApiResponse<T>;
  return response.data;
}

/**
 * 封装 PUT 请求
 * @param url 请求地址
 * @param data 请求体数据
 * @param config 额外配置
 * @returns 响应数据（ApiResponse.data 部分）
 */
export async function put<T>(
  url: string,
  data?: Record<string, unknown>,
  config?: AxiosRequestConfig
): Promise<T> {
  // 响应拦截器返回的是 ApiResponse 对象，这里需要类型断言
  const response = await instance.put(url, data, config) as unknown as ApiResponse<T>;
  return response.data;
}

/**
 * 封装 DELETE 请求
 * @param url 请求地址
 * @param config 额外配置
 * @returns 响应数据（ApiResponse.data 部分）
 */
export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  // 响应拦截器返回的是 ApiResponse 对象，这里需要类型断言
  const response = await instance.delete(url, config) as unknown as ApiResponse<T>;
  return response.data;
}

/**
 * 封装 PATCH 请求
 * @param url 请求地址
 * @param data 请求体数据
 * @param config 额外配置
 * @returns 响应数据（ApiResponse.data 部分）
 */
export async function patch<T>(
  url: string,
  data?: Record<string, unknown>,
  config?: AxiosRequestConfig
): Promise<T> {
  // 响应拦截器返回的是 ApiResponse 对象，这里需要类型断言
  const response = await instance.patch(url, data, config) as unknown as ApiResponse<T>;
  return response.data;
}

/**
 * 封装文件上传请求
 * @param url 请求地址
 * @param file 文件对象
 * @param onProgress 上传进度回调
 * @returns 响应数据（ApiResponse.data 部分）
 */
export async function upload<T>(
  url: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<T> {
  const formData = new FormData();
  formData.append('file', file);

  // 响应拦截器返回的是 ApiResponse 对象，这里需要类型断言
  const response = await instance.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: event => {
      if (event.total && onProgress) {
        const percent = Math.round((event.loaded * 100) / event.total);
        onProgress(percent);
      }
    }
  }) as unknown as ApiResponse<T>;

  return response.data;
}

// ============================================
// 带正确类型的请求对象
// ============================================

/**
 * 带正确类型的请求对象
 * 响应拦截器返回 ApiResponse 对象，而不是 AxiosResponse
 * 使用此对象可以直接访问 res.code 和 res.data
 */
export const request = {
  /**
   * GET 请求，返回完整的 ApiResponse 对象
   */
  get: async <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return instance.get(url, config) as unknown as Promise<ApiResponse<T>>;
  },
  
  /**
   * POST 请求，返回完整的 ApiResponse 对象
   */
  post: async <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return instance.post(url, data, config) as unknown as Promise<ApiResponse<T>>;
  },
  
  /**
   * PUT 请求，返回完整的 ApiResponse 对象
   */
  put: async <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return instance.put(url, data, config) as unknown as Promise<ApiResponse<T>>;
  },
  
  /**
   * DELETE 请求，返回完整的 ApiResponse 对象
   */
  delete: async <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return instance.delete(url, config) as unknown as Promise<ApiResponse<T>>;
  },
  
  /**
   * PATCH 请求，返回完整的 ApiResponse 对象
   */
  patch: async <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return instance.patch(url, data, config) as unknown as Promise<ApiResponse<T>>;
  },
};

export default instance;
